<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Main Layout: Image Left + Form Right -->
                <div class="row g-4">
                    <!-- Left Column - Photo Gallery -->
                    <div class="col-12 col-xl-4">
                        <div class="sticky-top" style="top: 20px;">
                            <div role="group" class="form-group">
                                <label class="form-label fw-bold mb-3">{{ $t('gallery') }}</label>
                                <div>
                                    <GalleryPicker class="gallery-picker" v-model="products.photo"
                                        :ratioWidth="ratioWidth"
                                        :ratioHeight="ratioHeight"
                                        @update:ratioWidth="ratioWidth = $event"
                                        @update:ratioHeight="ratioHeight = $event"
                                        v-bind:class="{'is-invalid': genericValidator(products.photo) === false || errorValidator('Photo') === false }" 
                                        :max="1">
                                    </GalleryPicker>
                                    <div v-if="errorOutput['Photo']" class="invalid-feedback">{{errorOutput['Photo']}}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Form Fields -->
                    <div class="col-12 col-xl-8">                        
                        <!-- Basic Information Section -->
                        <div class="mb-4">
                            <!-- Name Section -->
                            <div role="group" class="form-group">
                                <label class="form-label fw-bold">{{ $t('nameMandatory') }}</label>
                                <VueEditor v-model="products.names[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(products.names) == false || errorValidator('Names') }"></VueEditor>
                                <div v-if="errorOutput['Names']" class="invalid-feedback">{{errorOutput['Names']}}</div>
                            </div>
                            <div class="row g-3">
                                <!-- Type -->
                                <div class="col-12 col-md-6">
                                    <div role="group" class="form-group">
                                        <label class="form-label">{{ $t('type') }}</label>
                                        <select class="form-control" v-model="products.type" name="type" id="type">
                                            <option value="">{{ $t('filterByType') }}</option>
                                            <option value="Wine">{{ $t('wines') }}</option>
                                            <option value="Other">{{ $t('other') }}</option>
                                        </select>
                                    </div>
                                </div>
                                <!-- Year -->
                                <div class="col-12 col-md-6">
                                    <div role="group" class="form-group">
                                        <label class="form-label">{{ $t('year') }}</label>
                                        <CInput type="text" v-model="products.year"></CInput>
                                    </div>
                                </div>
                                <!-- Position -->
                                <div class="col-12">
                                    <div role="group" class="form-group">
                                        <label class="form-label">{{ $t('positionOptional') }}</label>
                                        <CInput type="number" v-model="products.position" :invalid-feedback="errorOutput['Position']"
                                        :is-valid="errorValidator('Position')"></CInput>
                                    </div>
                                </div>
                            </div>
                            <!-- Description -->
                            <div class="mb-4">
                                <div role="group" class="form-group">
                                    <label class="form-label fw-bold">{{ $t('descriptionMandatory') }}</label>
                                    <VueEditor v-model="products.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(products.descriptions) == false || errorValidator('Descriptions') }"></VueEditor>
                                    <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Additional Information Section -->
                        <div class="mb-4">
                            <div class="d-flex align-items-center mb-3">
                                <CButton color="primary" size="sm" @click="addProductInfo()">
                                    <i class="cil-plus"></i> {{ $t('addProductInfo') }}
                                </CButton>
                            </div>
                            
                            <div v-if="products.mainProductInfos && products.mainProductInfos.length > 0" class="border rounded p-3 bg-light">
                                <div v-for="(productInfo, index) in products.mainProductInfos" :key="index" class="row g-2 mb-3">
                                    <div class="col-12 col-md-5">
                                        <CInput
                                            type="text"
                                            :placeholder="`${$t('title')}`"
                                            v-model="productInfo.keys[currentLanguage.key]"
                                            v-bind:class="{'is-invalid': genericValidator(productInfo.keys) == false || errorValidator('KEYS') }"
                                        />
                                        <div v-if="errorOutput['KEYS']" class="invalid-feedback">{{errorOutput['KEYS']}}</div>
                                    </div>
                                    <div class="col-12 col-md-5">
                                        <CInput
                                            type="text"
                                            :placeholder="`${$t('value')}`"
                                            v-model="productInfo.values[currentLanguage.key]"
                                            v-bind:class="{'is-invalid': genericValidator(productInfo.values) == false || errorValidator('VALUES') }"
                                        />
                                        <div v-if="errorOutput['VALUES']" class="invalid-feedback">{{errorOutput['VALUES']}}</div>
                                    </div>
                                    <div class="col-12 col-md-2 d-flex justify-content-end align-self-start">
                                        <CButton color="danger" size="sm" @click="removeProductInfo(index)">
                                            <i class="cil-trash"></i> {{ $t('delete') }}
                                        </CButton>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center py-3 border rounded bg-light">
                                <i class="cil-info"></i> {{ $t('noAdditionalInfo') }}
                            </div>
                        </div>

                        <!-- Datasheet Section -->
                        <div class="mb-4">
                            <CustomPicker 
                                v-for="language in supportedLanguages" 
                                :key="`datasheet-${language.key}`" 
                                v-show="language.key == currentLanguage.key" 
                                :identifier="language.key" 
                                :title="$t('datasheet')" 
                                class="form-group" 
                                v-model="products.datasheets[language.key]" 
                                v-bind:class="{'is-invalid': errorValidator('Datasheets')}">
                            </CustomPicker>
                        </div>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- submitting -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import Product from "@/classes/Product";
import { mapState } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import CustomPicker from '@/controls/CustomPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, CustomPicker, FormLoading },
    props:{    
        baseProducts:
        {
            type: Object,
            default: null,
        },
        baseProductsName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            products: new Product({}, {}, {}, {}, 0, 0, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
            mainProductInfos: [],
            type: "",
            ratioWidth: 2, // default ratio width
            ratioHeight: 7,  // default ratio height
        };
    },
    mounted: function()
    {
        this.resetProducts();
    },
    computed:
    {
        supportedLanguages: function()
        {
            return CONSTANTS.supportedLanguages;
        },
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function() {
            let result = {};

            // Validate alerts
            if (this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
                result = this.alertOutput.reduce((accum, currentVal) => {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate photo is required
            if (!this.products.photo || !this.products.photo.url || !this.products.photo.url.trim()) {
                result['Photo'] = this.$i18n.t("photoRequired");
            }

            // Validate multilanguage fields for all supported languages
            for (const lang of this.supportedLanguages) {
                const langKey = lang.key;

                if (!this.products.names[langKey] || !this.products.names[langKey].trim()) {
                    result['Names'] = this.$i18n.t("validateBothTranslations", { lang: langKey });
                }
                if (!this.products.descriptions[langKey] || !this.products.descriptions[langKey].trim()) {
                    result['Descriptions'] = this.$i18n.t("validateBothTranslations", { lang: langKey });
                }
            }

            // Validate mainProductInfos for both languages (only if there are items added)
            if (this.products.mainProductInfos && this.products.mainProductInfos.length > 0) {
                for (const [, productInfo] of this.products.mainProductInfos.entries()) {
                    let hasEmptyKey = false;
                    let hasEmptyValue = false;
                    
                    for (const lang of this.supportedLanguages) {
                        const langKey = lang.key;

                        if (!productInfo.keys[langKey] || !productInfo.keys[langKey].trim()) {
                            hasEmptyKey = true;
                        }
                        if (!productInfo.values[langKey] || !productInfo.values[langKey].trim()) {
                            hasEmptyValue = true;
                        }
                    }
                    
                    if (hasEmptyKey) {
                        result[`KEYS`] = this.$i18n.t("validateKeyForTranslation");
                    }
                    if (hasEmptyValue) {
                        result[`VALUES`] = this.$i18n.t("validateValueForTranslation");
                    }
                }
            }

            return result;
        },

        ...mapState('products', ['postProductOutput']),
        ...mapState('products', ['putProductOutput']),
        name: function()
        {
            return this.baseProducts || this.baseProductsName ? (this.baseProducts ? this.baseProducts.name : this.baseProductsName) : null;
        }
    },
    methods:{
        resetProducts() {
            if (!this.baseProducts) {
                const names = {};
                const descriptions = {};
                CONSTANTS.supportedLanguages.forEach(language => { 
                    names[language.key] = ""; 
                    descriptions[language.key] = ""; 
                });

                this.products = new Product(names, descriptions, {}, []);
            } else {
                this.products = deepClone(new Product, this.baseProducts);

                this.products.mainProductInfos = this.products.mainProductInfos.map(info => ({
                    ...info,
                    keys: info.keys || {},
                    values: info.values || {},
                }));

                CONSTANTS.supportedLanguages.forEach(language => {
                    this.products.mainProductInfos.forEach(info => {
                        this.$set(info.keys, language.key, info.keys[language.key] || '');
                        this.$set(info.values, language.key, info.values[language.key] || '');
                    });
                });

                this.products.photo = { url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.products.photoPath]) };
            }

            // Ensure Vue reactivity system tracks changes to mainProductInfos
            this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
        },

        addProductInfo() {
            if (!this.products.mainProductInfos) {
                this.products.mainProductInfos = [];
            }
            this.products.mainProductInfos.push({
                keys: {
                    pt: '',
                    en: ''
                },
                values: {
                    pt: '',
                    en: ''
                }
            });
            
            // Ensure Vue reactivity system tracks changes to mainProductInfos
            this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
        },
        removeProductInfo(index) {
            if (Array.isArray(this.products.mainProductInfos) && this.products.mainProductInfos.length > index) {
                this.products.mainProductInfos.splice(index, 1);
                
                // Ensure Vue reactivity system tracks changes to mainProductInfos
                this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
            }
        },
        submitClicked() {
            this.triedSubmit = true;

            if (!this.products) {
                console.error('Product is undefined or null');
                return;
            }

            console.log('Product before submission:', JSON.stringify(this.products, null, 2));

            if (this.products.isValid(this.currentLanguage.key)) {
                this.submitting = true;
                this.$emit('submit-requested', this.products);
                console.log('Submitting Product:', this.products);
            } else {
                console.log('Submit Validation Failed');
            }
        },

        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "ProductsList" });
        },
        genericValidator,
        errorValidator
    },
    watch:
    {
        postProductOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putProductOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseProducts: function()
        {
            this.resetProducts();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 